// Student spreadsheet import — pure helpers (School Bus Standard, spec §6.1).
// detectColumns is a TWIN of server/src/school-bus/import-rows.ts; both are pinned by
// server/tests/fixtures/school-bus-import/headers.json. Phones stay raw here — the server normalises them.
import { parseCsvText } from '@/features/fuel/purchaseImport';

export const COLUMNS = ['name', 'classLabel', 'bus', 'stop', 'phone', 'phone2', 'externalRef'];

// Order matters: a header takes the first kind that matches ("phone number" is a phone, "bus ID" is a bus; id/number only count once nothing else claims the header).
const KINDS = [
  { kind: 'phone', re: /\b(phone|mobile|tel)|الهاتف|هاتف|موبايل|مۆبایل|تەلەفۆن/ },
  { kind: 'classLabel', re: /\b(class|grade)|الصف|پۆل/ },
  { kind: 'bus', re: /\bbus\b|الباص|پاس/ },
  { kind: 'stop', re: /\bstop\b|المحطة|وێستگە/ },
  { kind: 'externalRef', re: /\b(id|student\s*(number|no|id)|number)\b|الرقم|ژمارە/ },
  { kind: 'name', re: /\bname\b|الاسم|ناو/ },
];

/** headers → { name?, classLabel?, bus?, stop?, phone?, phone2?, externalRef? } column indexes. */
export function detectColumns(headers) {
  const map = {};
  headers.forEach((h, idx) => {
    const text = String(h ?? '').trim().toLowerCase();
    if (!text) return;
    const hit = KINDS.find((k) => k.re.test(text));
    if (!hit) return;
    if (hit.kind === 'phone') {
      if (map.phone === undefined) map.phone = idx;
      else if (map.phone2 === undefined) map.phone2 = idx;
    } else if (map[hit.kind] === undefined) {
      map[hit.kind] = idx;
    }
  });
  return map;
}

/** Detected columns with the office's choices on top: a number sets a column, null clears it, undefined keeps the detected one. */
export function resolveColumns(headers, overrides = {}) {
  const map = detectColumns(headers);
  for (const col of COLUMNS) {
    const v = overrides[col];
    if (v === null) delete map[col];
    else if (Number.isInteger(v)) map[col] = v;
  }
  return map;
}

function cell(cells, idx) {
  if (idx === undefined) return null;
  const v = String(cells[idx] ?? '').trim();
  return v === '' ? null : v;
}

/** One spreadsheet row → { ok: true, value: StudentImportRow } | { ok: false, error: 'name' }. */
export function mapRow(cells, map) {
  const name = cell(cells, map.name);
  if (!name) return { ok: false, error: 'name' };
  const phones = [];
  for (const idx of [map.phone, map.phone2]) {
    const p = cell(cells, idx);
    if (p && !phones.includes(p)) phones.push(p);
  }
  return {
    ok: true,
    value: {
      name,
      classLabel: cell(cells, map.classLabel),
      busName: cell(cells, map.bus),
      stopName: cell(cells, map.stop),
      phones,
      externalRef: cell(cells, map.externalRef),
    },
  };
}

/** Web twin of server/src/school-bus/phone.ts normalizePhone (E.164, Iraq default). Invalid → null. */
export function normalizePhone(raw, defaultCountry = '964') {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim().replace(/[٠-٩۰-۹]/g, (d) => String(d.charCodeAt(0) - (d.charCodeAt(0) >= 0x06f0 ? 0x06f0 : 0x0660)));
  if (!/^[+\d\s\-().]+$/.test(trimmed)) return null;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 8) return null;
  let full;
  if (trimmed.startsWith('+')) full = digits;
  else if (digits.startsWith('00')) full = digits.slice(2);
  else if (digits.startsWith('0')) full = defaultCountry + digits.slice(1);
  else if (digits.startsWith(defaultCountry) && digits.length >= defaultCountry.length + 8) full = digits;
  else full = defaultCountry + digits;
  return full.length > 15 ? null : `+${full}`;
}

/**
 * Approximate count of parents the import will text: distinct valid numbers in the rows that are not already on a
 * student the caller can see (`knownPhones`, E.164). The server decides for real (a number already in the org — even
 * on a student out of view — gets no SMS).
 */
export function estimateNewPhones(rows, knownPhones = []) {
  const known = new Set(knownPhones);
  const fresh = new Set();
  for (const r of rows) for (const p of r.phones ?? []) {
    const e164 = normalizePhone(p);
    if (e164 && !known.has(e164)) fresh.add(e164);
  }
  return fresh.size;
}

/** The rows the server will be sent (rows without a name are left out). */
export function mapRows(headers, rows, overrides) {
  const map = resolveColumns(headers, overrides);
  return rows.map((r) => mapRow(r, map)).filter((r) => r.ok).map((r) => r.value);
}

const key = (s) => String(s ?? '').trim().toLowerCase();

/**
 * Bus names in the file that match no visible bus, or more than one (the server then leaves the row without a bus).
 * `buses` = [{ id, name }] — vehicle names and parent-link labels both count. Each name is listed once, first spelling kept.
 */
export function busMatches(rows, buses) {
  const ids = new Map();
  for (const b of buses) {
    const k = key(b.name);
    if (!k) continue;
    if (!ids.has(k)) ids.set(k, new Set());
    ids.get(k).add(b.id);
  }
  const seen = new Set();
  const unmatched = [];
  const ambiguous = [];
  for (const r of rows) {
    const k = key(r.busName);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    const n = ids.get(k)?.size ?? 0;
    if (n === 0) unmatched.push(r.busName);
    else if (n > 1) ambiguous.push(r.busName);
  }
  return { unmatched, ambiguous };
}

/** .csv / .xlsx / .xls → { headers, rows } with every cell a trimmed string; blank rows dropped. */
export async function parseStudentFile(file) {
  const ext = String(file?.name ?? '').split('.').pop().toLowerCase();
  if (ext === 'csv') return parseCsvText(await file.text());
  if (ext === 'xlsx' || ext === 'xls') {
    const XLSX = await import('xlsx');
    const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const data = ws ? XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' }) : [];
    const str = (r) => (Array.isArray(r) ? r : []).map((c) => String(c ?? '').trim());
    const [headers = [], ...rows] = data.map(str);
    return { headers, rows: rows.filter((r) => r.some((c) => c !== '')) };
  }
  throw new Error('unsupported_file');
}

/** The stable error code of a failed /api/school-bus call ({ error } bodies carry no `code`). */
export function apiErrorCode(e) {
  return e?.code || e?.body?.error || null;
}
